import React from 'react';
import { Phone, Mail, MapPin } from 'lucide-react';
import { businessInfo } from '../utils/mock';
import { Card, CardContent } from './ui/card';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-black border-t border-zinc-900">
      <div className="container mx-auto px-4 py-8 md:py-12">
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {/* Brand */}
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <img 
                src="https://customer-assets-0z36b82j.emergentagent.net/job_mobile-detail-site/artifacts/r8awla6t_IMG_6242.jpeg"
                alt="Mobile Shine Detailing Logo"
                className="h-20 w-20 object-contain"
              />
              <div>
                <h3 className="text-2xl font-bold text-white">{businessInfo.name}</h3>
                <p className="text-red-500 font-semibold text-sm">{businessInfo.tagline}</p>
              </div>
            </div>
            <p className="text-gray-400 leading-relaxed">
              Professional mobile detailing services bringing showroom quality to your driveway.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold text-lg mb-4">Quick Links</h4>
            <nav className="space-y-2">
              <button
                onClick={() => scrollToSection('booking')}
                className="block w-full text-left px-4 py-2.5 text-white font-bold bg-red-600/20 border-2 border-red-500 rounded-lg hover:bg-red-600 hover:border-red-600 transition-all duration-300 hover:scale-105"
              >
                📅 Book Now
              </button>
              <button
                onClick={() => scrollToSection('services')}
                className="block text-gray-400 hover:text-red-500 transition-colors"
              >
                Services
              </button>
              <button
                onClick={() => scrollToSection('gallery')}
                className="block text-gray-400 hover:text-red-500 transition-colors"
              >
                Gallery
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="block text-gray-400 hover:text-red-500 transition-colors"
              >
                Contact
              </button>
              <a
                href="https://g.page/r/CS8IeFV59cyJEBM/review"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-gray-400 hover:text-red-500 transition-colors flex items-center gap-2"
              >
                ⭐ Leave a Review
              </a>
            </nav>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold text-lg mb-4">Contact Info</h4>
            <div className="space-y-3">
              <a
                href={`tel:${businessInfo.phone}`}
                className="flex items-center space-x-3 text-gray-400 hover:text-red-500 transition-colors group"
              >
                <Phone className="w-5 h-5 text-red-500 group-hover:scale-110 transition-transform" />
                <span>{businessInfo.phone}</span>
              </a>
              <a
                href={`mailto:${businessInfo.email}`}
                className="flex items-center space-x-3 text-gray-400 hover:text-red-500 transition-colors group"
              >
                <Mail className="w-5 h-5 text-red-500 group-hover:scale-110 transition-transform" />
                <span>{businessInfo.email}</span>
              </a>
              <div className="flex items-center space-x-3 text-gray-400">
                <MapPin className="w-5 h-5 text-red-500" />
                <span>{businessInfo.address}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Google Review CTA - Enhanced Prominence */}
        <div className="mt-6 md:mt-10 mb-4 md:mb-6">
          <Card className="bg-gradient-to-r from-red-600/20 to-red-700/10 border-2 border-red-500/40 hover:border-red-500/60 transition-all">
            <CardContent className="p-4 md:p-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6">
                <div className="text-center md:text-left">
                  <div className="flex items-center justify-center md:justify-start space-x-2 mb-1 md:mb-2">
                    <span className="text-2xl md:text-3xl">⭐⭐⭐⭐⭐</span>
                  </div>
                  <h3 className="text-white font-bold text-xl md:text-2xl mb-1 md:mb-2">Love Mobile Shine?</h3>
                  <p className="text-gray-300 text-sm md:text-base">
                    Share your experience and help others discover our premium detailing services!
                  </p>
                </div>
                <a
                  href="https://g.page/r/CS8IeFV59cyJEBM/review"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 md:gap-3 bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 md:py-4 md:px-10 rounded-lg transition-all duration-300 hover:scale-105 shadow-xl hover:shadow-red-500/50 whitespace-nowrap text-sm md:text-base"
                >
                  <span className="text-xl md:text-2xl">📝</span>
                  <span>Leave a Review</span>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-zinc-900 mt-6 md:mt-8 pt-6 md:pt-8 text-center">
          <p className="text-gray-400 text-sm">
            © {currentYear} {businessInfo.name}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
